import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  MouseEvent,
} from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import HistoryIcon from "@mui/icons-material/History";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_DensityState,
  type MRT_Row,
  MRT_SortingState,
  MRT_VisibilityState,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { showDeleteConfirmModal } from "../../Components/ConfirmModal";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { CostObject, DailyRow, MonthlyRow } from "../../Types/CashFlow.type";

export const MONTHLY_DUMMY: MonthlyRow[] = [
  { month: "Jan-2026", cash_in: 60000, cash_out: -10000, net: 50000 },
  { month: "Feb-2026", cash_in: 20000, cash_out: -10000, net: 10000 },
  { month: "Mar-2026", cash_in: 45000, cash_out: -15000, net: 30000 },
  { month: "Apr-2026", cash_in: 80000, cash_out: -25000, net: 55000 },
];

// ─── Utility: build column config (same as contract table) ───────────────────
const createColumnConfig = (
  accessorKey: string,
  header: string,
  validationErrors: Record<string, string | undefined>,
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>,
  overrides: Partial<MRT_ColumnDef<any>> = {}
): MRT_ColumnDef<any> => ({
  accessorKey,
  header,
  enableEditing: false,
  filterVariant: "text",
  muiTableBodyCellProps: ({ row, table }) => ({
    onDoubleClick: (e: any) => e.stopPropagation(),
    sx: {
      cursor: "pointer",
      "&:hover": { backgroundColor: "rgba(44, 4, 4, 0.05)" },
      ...(table.getState().editingRow?.id === row.id && {
        backgroundColor: "rgba(0, 128, 0, 0.1)",
        transition: "background-color 0.3s ease",
      }),
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  }),
  muiEditTextFieldProps: {
    name: accessorKey,
    required: true,
    error: !!validationErrors?.[accessorKey],
    helperText: validationErrors?.[accessorKey],
    onFocus: () =>
      setValidationErrors((prev) => ({ ...prev, [accessorKey]: undefined })),
  },
  ...overrides,
});

// ─── Table Inner Component ────────────────────────────────────────────────────
const CostObjectTableInner = ({
  dataVersion,
  setDataVersion,
  setCopiedData,
  setEditMode,
  setEditedData,
  viewMode,
  groupByDueDate,
  formFilters,
  onDailySummaryChange,
}: {
  dataVersion: number;
  setDataVersion: React.Dispatch<React.SetStateAction<number>>;
  setCopiedData: (data: any) => void;
  setEditMode: (v: boolean) => void;
  setEditedData: (data: any) => void;
  viewMode: "daily" | "monthly";
  groupByDueDate: boolean;
  formFilters: Record<string, string>;
  onDailySummaryChange?: (summary: { cashIn: number; cashOut: number; net: number }) => void;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isFirstRender = useRef(true);

  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 15 });
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>({});
  const [density, setDensity] = useState<MRT_DensityState>("comfortable");
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);
  const [showGlobalFilter, setShowGlobalFilter] = useState(true);
  const [showColumnFilters, setShowColumnFilters] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [filterModes, setFilterModes] = useState<{ mode: string; id: string; value: unknown }[]>([]);
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [dailyGrouping, setDailyGrouping] = useState<string[]>([]);
  const [showRowResizing, setShowRowResizing] = useState<boolean>(true);

  useEffect(() => {
    const stored = {
      columnFilters: sessionStorage.getItem("mrt_co_columnFilters"),
      columnVisibility: sessionStorage.getItem("mrt_co_columnVisibility"),
      density: sessionStorage.getItem("mrt_co_density"),
      globalFilter: sessionStorage.getItem("mrt_co_globalFilter"),
      showGlobalFilter: sessionStorage.getItem("mrt_co_showGlobalFilter"),
      showColumnFilters: sessionStorage.getItem("mrt_co_showColumnFilters"),
      sorting: sessionStorage.getItem("mrt_co_sorting"),
    };
    if (stored.columnFilters) setColumnFilters(JSON.parse(stored.columnFilters));
    if (stored.columnVisibility) setColumnVisibility(JSON.parse(stored.columnVisibility));
    if (stored.density) setDensity(JSON.parse(stored.density));
    if (stored.globalFilter) setGlobalFilter(JSON.parse(stored.globalFilter) || undefined);
    if (stored.showGlobalFilter) setShowGlobalFilter(JSON.parse(stored.showGlobalFilter));
    if (stored.showColumnFilters) setShowColumnFilters(JSON.parse(stored.showColumnFilters));
    if (stored.sorting) setSorting(JSON.parse(stored.sorting));
    isFirstRender.current = false;
  }, []);

  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_columnFilters", JSON.stringify(columnFilters)); }, [columnFilters]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_columnVisibility", JSON.stringify(columnVisibility)); }, [columnVisibility]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_density", JSON.stringify(density)); }, [density]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_globalFilter", JSON.stringify(globalFilter ?? "")); }, [globalFilter]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_showGlobalFilter", JSON.stringify(showGlobalFilter)); }, [showGlobalFilter]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_showColumnFilters", JSON.stringify(showColumnFilters)); }, [showColumnFilters]);
  useEffect(() => { if (!isFirstRender.current) sessionStorage.setItem("mrt_co_sorting", JSON.stringify(sorting)); }, [sorting]);

  // ─── Columns ─────────────────────────────────────────────────────────────
  const columns = useMemo<MRT_ColumnDef<CostObject>[]>(
    () => [
      createColumnConfig("currency", "Currency", validationErrors, setValidationErrors, {
        size: 80,
        Cell: ({ cell }: any) => <span style={{ fontSize: "0.72rem" }}>{cell.getValue()}</span>,
      }),
      createColumnConfig("due_date", "Due Date", validationErrors, setValidationErrors, {
        size: 100,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem" }}>{cell.getValue() || "—"}</span>
        ),
      }),
      createColumnConfig("book", "Book", validationErrors, setValidationErrors, {
        size: 80,
        Cell: ({ cell }: any) => <span style={{ fontSize: "0.72rem" }}>{cell.getValue()}</span>,
      }),
      createColumnConfig("trader", "Trader", validationErrors, setValidationErrors, {
        size: 90,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem", textTransform: "capitalize" }}>
            {cell.getValue()}
          </span>
        ),
      }),
      createColumnConfig("strategy", "Strategy", validationErrors, setValidationErrors, {
        size: 90,
        Cell: ({ cell }: any) => <span style={{ fontSize: "0.72rem" }}>{cell.getValue()}</span>,
      }),
      createColumnConfig("clearer", "Clearer", validationErrors, setValidationErrors, {
        size: 80,
        Cell: ({ cell }: any) => <span style={{ fontSize: "0.72rem" }}>{cell.getValue()}</span>,
      }),
      createColumnConfig("broker", "Broker", validationErrors, setValidationErrors, {
        size: 80,
        Cell: ({ cell }: any) => <span style={{ fontSize: "0.72rem" }}>{cell.getValue()}</span>,
      }),
      createColumnConfig("product", "Product", validationErrors, setValidationErrors, {
        size: 90,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem", textTransform: "capitalize" }}>
            {cell.getValue()}
          </span>
        ),
      }),
      createColumnConfig("counterparty", "Counterparty", validationErrors, setValidationErrors, {
        size: 110,
        Cell: ({ cell }: any) => <span style={{ fontSize: "0.72rem" }}>{cell.getValue()}</span>,
      }),
      createColumnConfig("requires_settlement", "Req. Settlement", validationErrors, setValidationErrors, {
        size: 130,
        Cell: ({ cell }: any) => (
          <Chip
            label={cell.getValue() ? "Yes" : "No"}
            size="small"
            color={cell.getValue() ? "success" : "default"}
            sx={{ fontSize: "0.65rem", height: "18px" }}
          />
        ),
      }),
      createColumnConfig("exclude_from_pnl", "Excl. PNL", validationErrors, setValidationErrors, {
        size: 100,
        Cell: ({ cell }: any) => (
          <Chip
            label={cell.getValue() ? "Yes" : "No"}
            size="small"
            color={cell.getValue() ? "warning" : "default"}
            sx={{ fontSize: "0.65rem", height: "18px" }}
          />
        ),
      }),
      createColumnConfig("description", "Description", validationErrors, setValidationErrors, {
        size: 180,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem", color: cell.getValue() ? "inherit" : "#999" }}>
            {cell.getValue() || "—"}
          </span>
        ),
      }),
    ],
    [validationErrors]
  );

  // ─── Data query (same React Query pattern as contract table) ─────────────
  const {
    data,
    isError: isLoadingUsersError,
    isFetching: isFetchingUsers,
    isLoading: isLoadingUsers,
  } = useQuery<{ results: CostObject[]; totalCount: number }>({
    queryKey: ["cost_objects", pagination, sorting, dataVersion],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: `${pagination.pageIndex + 1}`,
        page_size: `${pagination.pageSize}`,
      });
      if (sorting.length > 0) {
        params.append(
          `sortField_${sorting[0].id}`,
          `${sorting[0].desc ? "desc" : "asc"}_sorting`
        );
      }
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/cost-booking/?${params.toString()}`
      );
      const results: CostObject[] = response.data?.results ?? response.data ?? [];
      const count: number = response.data?.count ?? results.length;
      setTotalCount(count);
      return { results, totalCount: count };
    },
    refetchOnWindowFocus: false,
    placeholderData: (old) => old,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // ─── Daily API query — real backend ──────────────────────────────────────
  const {
    data: dailyApiData,
    isLoading: isDailyLoading,
    isFetching: isDailyFetching,
    isError: isDailyError,
  } = useQuery<DailyRow[]>({
    queryKey: ["cash_flow_daily", dataVersion],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/cash-flow-summary-excel/`
      );
      // API returns a plain array: [{ due_date, cash_in, cash_out, total_cash_flow }, ...]
      const results = response.data ?? [];
      // Map total_cash_flow → net so column definitions stay unchanged
      return results.map((item: any) => ({
        due_date: item.due_date ?? "",
        cash_in: item.cash_in ?? null,
        cash_out: item.cash_out ?? null,
        net: item.total_cash_flow ?? 0,
      }));
    },
    enabled: viewMode === "daily", // only fetch when daily tab is active
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // ─── Delete mutation ──────────────────────────────────────────────────────
  const { mutateAsync: deleteItem, isPending: isDeletingUser } = useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(
        `${backendbaseUrl}/paper-app/cost-booking/Bulk-Delete/`,
        { data: { id: [id] } }
      );
      return id;
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["cost_objects"] });
      const prev = queryClient.getQueryData(["cost_objects"]);
      return { prev };
    },
    onError: (err, id, context: any) => {
      queryClient.setQueryData(["cost_objects"], context?.prev);
      toast.error("Failed to delete. Please try again.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cost_objects"] });
    },
  });

  // ─── Row actions (same as contract table) ────────────────────────────────
  const contractEditFunctionality = (row: MRT_Row<CostObject>) => {
    const rowData = row.original;
    const textToCopy = JSON.stringify(rowData, null, 2);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(textToCopy).catch((err) => {
        console.error("Clipboard write failed:", err);
      });
    }
    setCopiedData(rowData);
    setEditMode(true);
    setEditedData(rowData);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCopyRow = (row: MRT_Row<CostObject>) => {
    if (!row) return;
    const rowData = row.original;
    const textToCopy = JSON.stringify(rowData, null, 2);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(textToCopy).catch((err) => {
        console.error("Clipboard write failed:", err);
      });
    }
    setCopiedData(rowData);
    setEditMode(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success("Copied and pasted successfully");
  };

  const openDeleteConfirmModal = (row: MRT_Row<CostObject>) => {
    showDeleteConfirmModal({
      title: "Are you sure you want to delete this Cost Object?",
      content: "Deleting...",
      onOk: () => {
        deleteItem(row.original.id)
          .then(() => {
            toast.success("Cost Object deleted successfully!");
            setDataVersion((prev) => prev + 1);
          })
          .catch(() => {
            toast.error("Failed to delete. Please try again.");
          });
      },
    });
  };

  // ─── Bulk delete ──────────────────────────────────────────────────────────
  const handleBulkDelete = async (rows: MRT_Row<CostObject>[]) => {
    const rowIds = rows.map((row) => row.original.id);
    if (rowIds.length === 0) {
      toast.warn("Please select at least one Cost Object to delete.");
      return;
    }

    showDeleteConfirmModal({
      title: `Delete ${rowIds.length} selected Cost Object(s)?`,
      content: `This will permanently delete ${rowIds.length} record(s). Continue?`,
      onOk: async () => {
        const loadingToast = toast.loading(`Deleting ${rowIds.length} Cost Object(s)...`);
        try {
          await axiosInstance.delete(
            `${backendbaseUrl}/paper-app/cost-booking/Bulk-Delete/`,
            { data: { id: rowIds } }
          );
          queryClient.invalidateQueries({ queryKey: ["cost_objects"] });
          table.setRowSelection({});
          setDataVersion((prev) => prev + 1);
          toast.success(`Successfully deleted ${rowIds.length} Cost Object(s)`);
        } catch (error: any) {
          const errorMessage =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message ||
            "Failed to delete Cost Objects";
          toast.error(`Deletion failed: ${errorMessage}`);
        } finally {
          toast.dismiss(loadingToast);
        }
      },
    });
  };

  // ─── Export all data ──────────────────────────────────────────────────────
  const handleDataDownload = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/cost-booking/`,
        { params: { page_size: 10000 } }
      );
      const all: CostObject[] = response.data?.results ?? response.data ?? [];
      if (all.length === 0) { toast.warn("No data to export"); return; }
      const headers = Object.keys(all[0]).join(",");
      const rows = all.map((row) =>
        Object.values(row).map((v) => `"${v ?? ""}"`).join(",")
      );
      const csv = [headers, ...rows].join("\n");
      const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "cost_objects.csv");
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
      toast.success("Cost Object data downloaded");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download data");
    }
  };

  const handleExportRows = (rows: MRT_Row<any>[]) => {
    const rowData = rows.map((row) => row.original);
    console.log(rowData);
  };

  const handleHistory = () => {
    navigate("/settings/log/history/costObjects");
  };

  // ─── Context menu (same as contract table) ───────────────────────────────
  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
  };
  const handleClose = () => setContextMenu(null);
  const handleResizing = () => { setShowRowResizing(!showRowResizing); handleClose(); };

  // ─── Table instance ───────────────────────────────────────────────────────
  const table = useMaterialReactTable<CostObject>({
    columns,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableGlobalFilter: true,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    enableRowSelection: true,
    enableColumnResizing: true,
    enableColumnFilterModes: true,
    enableFilterMatchHighlighting: true,
    enableDensityToggle: false,
    enableEditing: true,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: false,
    rowCount: totalCount,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onDensityChange: setDensity,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onSortingChange: setSorting,
    getRowId: (row) => row.id,
    data: data?.results || [],
    initialState: { showColumnFilters },

    state: {
      columnVisibility,
      sorting,
      columnFilters,
      pagination,
      isLoading: isLoadingUsers,
      isSaving: isDeletingUser,
      showAlertBanner: isLoadingUsersError,
      showProgressBars: isFetchingUsers,
      showColumnFilters,
      showGlobalFilter,
    },

    enableColumnOrdering: true,
    enableRowOrdering: false,

    filterFns: {
      customFilterFn: (row, id, filterValue) => row.getValue(id) === filterValue,
    },

    muiTopToolbarProps: () => ({
      sx: { backgroundColor: "#fff", padding: "8px" },
      className: "top-toolbar-wrapper__new",
    }),
    muiTableContainerProps: () => ({
      sx: { backgroundColor: "#fff" },
      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: () => ({
      sx: { backgroundColor: "#fff" },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: () => ({
      sx: { backgroundColor: "#fff", padding: "8px" },
      className: "bottom-toolbar-wrapper__new",
    }),

    muiTableBodyRowProps: ({ row, table }) => ({
      sx: {
        "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
        ...(table.getState().editingRow?.id === row.id && {
          backgroundColor: "rgba(0, 128, 0, 0.1)",
          transition: "background-color 0.3s ease",
        }),
      },
    }),

    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: "0", border: "0" },
    },
    muiToolbarAlertBannerProps: isLoadingUsersError
      ? { color: "error", children: "Error loading data" }
      : undefined,
    muiTableHeadCellProps: {
      sx: { border: "1px solid #e0e0e087", fontStyle: "normal", fontWeight: "normal" },
    },

    displayColumnDefOptions: {
      "mrt-row-actions": {
        enableColumnActions: true,
        size: 120,
      },
      "mrt-row-select": {
        enableColumnActions: true,
        enableHiding: true,
      },
    },

    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: async ({ table }) => { table.setEditingRow(null); },

    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Hidden CSV file input */}
        <input
          type="file"
          accept=".csv"
          id="upload-cost-object-csv"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const formData = new FormData();
              formData.append("file", file);
              Swal.fire({
                icon: "info",
                title: "Import",
                text: "CSV import will be connected to API when endpoint is ready.",
                timer: 2000,
                showConfirmButton: false,
              });
              e.target.value = "";
            }
          }}
        />

        {/* Import CSV */}
        {/* <Button
          variant="text"
          color="success"
          startIcon={<UploadFileIcon />}
          onClick={() => document.getElementById("upload-cost-object-csv")?.click()}
          sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.8, px: 1.2, borderRadius: "6px" }}
        >
          Import CSV
        </Button> */}

        {/* Export All Data */}
        <Button
          onClick={handleDataDownload}
          startIcon={<BrowserUpdatedIcon />}
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          Export All Data
        </Button>

        {/* History */}
        <Button
          onClick={handleHistory}
          startIcon={<HistoryIcon />}
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          History
        </Button>

        {/* Export Selected Rows */}
        <Button
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          Export Selected Rows
        </Button>

        {/* Bulk Delete */}
        {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
          <Button
            color="error"
            onClick={() => handleBulkDelete(table.getSelectedRowModel().rows)}
            startIcon={<DeleteIcon />}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            Delete Selected ({table.getSelectedRowModel().rows.length})
          </Button>
        )}
      </Box>
    ),

    renderRowActions: ({ row }) => (
      <Box>
        <Tooltip title="Edit" className="editIcon">
          <IconButton onClick={() => contractEditFunctionality(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" className="deleteIcon">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy Row">
          <IconButton onClick={() => handleCopyRow(row)}>
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  });

  useEffect(() => {
    const transformedFilters = columnFilters.map((filter) => ({
      ...filter,
      mode: table.getColumn(filter.id)?.getFilterFn()?.name || "contains",
    }));
    setFilterModes(transformedFilters);
  }, [columnFilters, table]);

  // ─── Daily columns ──────────────────────────────────────────────────────────
  const dailyColumns = useMemo<MRT_ColumnDef<DailyRow>[]>(
    () => [
      {
        accessorKey: "due_date",
        header: "Due Date",
        size: 280,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem" }}>{cell.getValue() || "—"}</span>
        ),
      },
      {
        accessorKey: "cash_in",
        header: "Cash In",
        size: 280,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem", color: "#2e7d32" }}>
            {cell.getValue() != null && cell.getValue() !== 0
              ? `+${Number(cell.getValue()).toLocaleString()}`
              : "—"}
          </span>
        ),
      },
      {
        accessorKey: "cash_out",
        header: "Cash Out",
        size: 280,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem", color: "#c62828" }}>
            {cell.getValue() != null && cell.getValue() !== 0
              ? `(${Math.abs(Number(cell.getValue())).toLocaleString()})`
              : "—"}
          </span>
        ),
      },
      {
        accessorKey: "net",
        header: "Net",
        size: 280,
        Cell: ({ cell }: any) => {
          const v = cell.getValue() as number;
          return (
            <span
              style={{
                fontSize: "0.72rem",
                fontWeight: "bold",
                color: v >= 0 ? "#2e7d32" : "#c62828",
              }}
            >
              {v >= 0 ? `+${v.toLocaleString()}` : v.toLocaleString()}
            </span>
          );
        },
      },
    ],
    []
  );

  // ─── Monthly columns ─────────────────────────────────────────────────────────
  const monthlyColumns = useMemo<MRT_ColumnDef<MonthlyRow>[]>(
    () => [
      {
        accessorKey: "month",
        header: "Month",
        size: 280,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem", fontWeight: "bold" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "cash_in",
        header: "Cash In",
        size: 280,
        Cell: ({ cell }: any) => (
          <span style={{ fontSize: "0.72rem", color: "#2e7d32" }}>
            {Number(cell.getValue()).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: "cash_out",
        header: "Cash Out",
        size: 280,
        Cell: ({ cell }: any) => {
          const v = cell.getValue() as number;
          return (
            <span style={{ fontSize: "0.72rem", color: "#c62828" }}>
              {`(${Math.abs(v).toLocaleString()})`}
            </span>
          );
        },
      },
      {
        accessorKey: "net",
        header: "Net",
        size: 280,
        Cell: ({ cell }: any) => {
          const v = cell.getValue() as number;
          return (
            <span
              style={{
                fontSize: "0.72rem",
                fontWeight: "bold",
                color: v >= 0 ? "#2e7d32" : "#c62828",
              }}
            >
              {v >= 0 ? `+${v.toLocaleString()}` : v.toLocaleString()}
            </span>
          );
        },
      },
    ],
    []
  );

  // ─── Sync grouping toggle ─────────────────────────────────────────────────
  useEffect(() => {
    setDailyGrouping(groupByDueDate ? ["due_date"] : []);
  }, [groupByDueDate]);

  // ─── Filter API data client-side using formFilters ────────────────────────
  const filteredDailyData = useMemo(() => {
    const source = dailyApiData ?? [];
    return source.filter((row) =>
      Object.entries(formFilters).every(([key, val]) => {
        if (!val) return true;
        const rowVal = String((row as any)[key] ?? "").toLowerCase().trim();
        const filterVal = String(val).toLowerCase().trim();
        return rowVal === filterVal;
      })
    );
  }, [dailyApiData, formFilters]);

  // ─── Push summary totals up to page (for form summary cards) ─────────────
  useEffect(() => {
    if (viewMode === "daily" && onDailySummaryChange) {
      onDailySummaryChange({
        cashIn: filteredDailyData.reduce((s, r) => s + (r.cash_in ?? 0), 0),
        cashOut: filteredDailyData.reduce((s, r) => s + (r.cash_out ?? 0), 0),
        net: filteredDailyData.reduce((s, r) => s + r.net, 0),
      });
    }
  }, [filteredDailyData, viewMode, onDailySummaryChange, dailyApiData]);

  // ─── Daily table instance ─────────────────────────────────────────────────
  const dailyTable = useMaterialReactTable<DailyRow>({
    columns: dailyColumns,
    data: filteredDailyData,
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableGlobalFilter: true,
    enableColumnPinning: true,
    enableDensityToggle: false,
    enableEditing: false,
    enableRowSelection: false,
    state: {
      grouping: dailyGrouping,
      isLoading: isDailyLoading,
      showProgressBars: isDailyFetching,
      showAlertBanner: isDailyError,
    },
    onGroupingChange: setDailyGrouping,
    muiToolbarAlertBannerProps: isDailyError
      ? { color: "error", children: "Error loading daily cash flow data" }
      : undefined,
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: "0", border: "0" } },
    muiTableHeadCellProps: {
      sx: { border: "1px solid #e0e0e087", fontWeight: "normal" },
    },
    muiTableHeadProps: {
    sx: {
      zIndex: 0,   
    },
  },
    muiTopToolbarProps: { sx: { backgroundColor: "#fff", padding: "8px" } },
    muiTableContainerProps: { sx: { backgroundColor: "#fff" } },
    muiBottomToolbarProps: { sx: { backgroundColor: "#fff" } },
    renderBottomToolbarCustomActions: () => {
      const totalNet = filteredDailyData.reduce((sum, r) => sum + r.net, 0);
      return (
        <Box sx={{ p: 1, fontWeight: "bold", fontSize: "0.75rem" }}>
          Total Net:{" "}
          <span style={{ color: totalNet >= 0 ? "#2e7d32" : "#c62828" }}>
            {totalNet >= 0 ? `+${totalNet.toLocaleString()}` : totalNet.toLocaleString()}
          </span>
        </Box>
      );
    },
  });

  // ─── Monthly table instance (unchanged — uses mock data) ──────────────────
  const monthlyTable = useMaterialReactTable<MonthlyRow>({
    columns: monthlyColumns,
    data: MONTHLY_DUMMY,
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableGlobalFilter: true,
    enableColumnPinning: true,
    enableDensityToggle: false,
    enableEditing: false,
    enableRowSelection: false,
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: "0", border: "0" } },
    muiTableHeadCellProps: {
      sx: { border: "1px solid #e0e0e087", fontWeight: "normal" },
    },
    muiTopToolbarProps: { sx: { backgroundColor: "#fff", padding: "8px" } },
    muiTableContainerProps: { sx: { backgroundColor: "#fff" } },
    muiBottomToolbarProps: { sx: { backgroundColor: "#fff" } },
    renderBottomToolbarCustomActions: () => {
      const totalNet = MONTHLY_DUMMY.reduce((sum, r) => sum + r.net, 0);
      return (
        <Box sx={{ p: 1, fontWeight: "bold", fontSize: "0.75rem" }}>
          Total Net:{" "}
          <span style={{ color: totalNet >= 0 ? "#2e7d32" : "#c62828" }}>
            {totalNet >= 0
              ? `+${totalNet.toLocaleString()}`
              : totalNet.toLocaleString()}
          </span>
        </Box>
      );
    },
  });

  return (
    <div className="table-ad__wrapper" onContextMenu={handleContextMenu}>
      <div className="table-ad__wrapperCard">
        <Box sx={{ mt: 2 }}>
          {viewMode === "daily" ? (
            <MaterialReactTable table={dailyTable} />
          ) : (
            <MaterialReactTable table={monthlyTable} />
          )}
        </Box>

        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem onClick={handleResizing}>
            {showRowResizing ? "Disable Resizing" : "Enable Resizing"}
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

const queryClient = new QueryClient();

export default function CostObjectTable({
  dataVersion,
  setDataVersion,
  setCopiedData,
  setEditMode,
  setEditedData,
  viewMode,
  groupByDueDate,
  formFilters,
  onDailySummaryChange,
}: {
  dataVersion: number;
  setDataVersion: React.Dispatch<React.SetStateAction<number>>;
  setCopiedData: (data: any) => void;
  setEditMode: (v: boolean) => void;
  setEditedData: (data: any) => void;
  viewMode: "daily" | "monthly";
  groupByDueDate: boolean;
  formFilters: Record<string, string>;
  onDailySummaryChange?: (summary: { cashIn: number; cashOut: number; net: number }) => void;
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <CostObjectTableInner
        dataVersion={dataVersion}
        setDataVersion={setDataVersion}
        setCopiedData={setCopiedData}
        setEditMode={setEditMode}
        setEditedData={setEditedData}
        viewMode={viewMode}
        groupByDueDate={groupByDueDate}
        formFilters={formFilters}
        onDailySummaryChange={onDailySummaryChange}
      />
    </QueryClientProvider>
  );
}